import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, isAxiosError } from "axios";

interface AjaxServerResponse<TResponseData, TErrorCode = string> {
    readonly error: boolean;
    readonly code?: TErrorCode;
    readonly message?: string;
    readonly data?: TResponseData;
}

export class MaxServerError<TError = string> extends Error {
    public readonly errorCode?: TError;

    constructor(message: string, errorCode?: TError) {
        super(message);
        this.name = "MaxServerError";
        this.errorCode = errorCode;
    }
}

export class MaxServerAxiosError extends MaxServerError {
    public readonly axiosError: AxiosError;

    constructor(message: string, axiosError: AxiosError) {
        super(message);
        this.name = "MaxServerAxiosError";
        this.axiosError = axiosError;
    }
}

export default new class AjaxClient {
    constructor() {
        this.setDefaultHeaders();
    }

    public async get<TResponseData = undefined>(url: string, config?: AxiosRequestConfig): Promise<TResponseData> {
        const usedConfig = this.mergeHeaders(config);

        return this.handleResponse<TResponseData>(axios.get<AjaxServerResponse<TResponseData>>(url, usedConfig), url);
    }

    public async post<TResponseData = undefined>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<TResponseData> {
        const usedConfig = this.mergeHeaders(config);
        usedConfig.headers["Content-Type"] = "application/x-www-form-urlencoded";

        return this.handleResponse(axios.post<AjaxServerResponse<TResponseData>>(url, data, usedConfig), url);
    }

    private setDefaultHeaders(): void {
        axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";
        axios.defaults.headers.post["Content-Type"] = "application/x-www-form-urlencoded";
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    private mergeHeaders(config?: AxiosRequestConfig): AxiosRequestConfig & { headers: { "X-Requested-With": "XMLHttpRequest" } } {
        return {
            ...config,
            headers: {
                "X-Requested-With": "XMLHttpRequest",
                // eslint-disable-next-line @typescript-eslint/no-misused-spread
                ...config?.headers,
            },
        };
    }

    private async handleResponse<T>(promise: Promise<AxiosResponse<AjaxServerResponse<T>>>, url: string): Promise<T> {
        const response = await this.fetchResponse(promise);

        if (typeof response.data !== "object" || !("error" in response.data)) {
            throw new MaxServerError(`No data received from server from request: ${JSON.stringify(response.request)} with response ${JSON.stringify(response)}`, "NO_DATA");
        }

        if (response.data.error) {
            throw new MaxServerError(
                response.data.message ?? `No response provided from server (url: ${url}, request: ${JSON.stringify(response.request)}): ${JSON.stringify(response)}`,
                response.data.code,
            );
        }

        if (response.data.data === undefined) {
            throw new MaxServerError(` No data provided in response (url: ${url}, request: ${JSON.stringify(response.request)}): ${JSON.stringify(response)}`, "NO_DATA");
        }

        return response.data.data;
    }

    private async fetchResponse<T>(promise: Promise<AxiosResponse<AjaxServerResponse<T>>>): Promise<AxiosResponse<AjaxServerResponse<T>>> {
        try {
            return await promise;
        } catch (error) {
            if (isAxiosError(error)) {
                throw new MaxServerAxiosError(error.message, error);
            }

            throw error;
        }
    }
}();
